import type { ReactNode } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/v2-components/ui/accordion";
import { Checkbox } from "@/v2-components/ui/checkbox";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/v2-components/ui/hover-card";
import { Badge } from "@/v2-components/ui/badge";
import KeyIcon from "@/assets/icons/KeyIcon";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";
import InfoIcon from "@/v2-components/core/icon/info-icon";
import RefreshIcon from "@/v2-components/core/icon/refresh-icon";
import VrGamesSearch from "@/v2-components/pages/games/vr-games/vr-games-search";
import { getFilterIcon } from "@/v2-components/pages/games/_utils";
import { SUPPORT_TYPE_DESC } from "@/v2-components/pages/games/_const";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import { useStickyBandBottom } from "@/v2-components/pages/games/use-sticky-band-bottom";
import type {
  VrFilterGroup,
  VrGamesFilterState,
} from "@/v2-components/pages/games/vr-games/use-vr-games-filters";

/**
 * `/games/vr/games` 좌측 필터 레일 (시안 `29889:130588`, 342×850).
 *
 * ## 기존 필터 시트와 무엇이 다른가
 *
 * `DesktopGameFilterSheet`(레거시 · #5689 가 삭제)는 **칩을 눌러 여는 오버레이**였다.
 * 이건 **상시 노출 레일**이고
 * 항목마다 **개수**가 붙는다. 그래서 같은 컴포넌트로 못 합쳤다 — 다만 안쪽 표현
 * (아코디언 · 체크박스 · `getFilterIcon` · Support Type 설명 호버)은 그대로 맞춰서
 * 두 화면의 필터가 다른 물건처럼 보이지 않게 한다.
 *
 * ## 개수가 0 인 항목
 *
 * 지우지 않고 **비활성으로 남긴다.** 사라지면 목록이 매번 들썩이고, 「VIVE 는 원래
 * 없는 건가 내가 뭘 걸어놔서 없는 건가」를 구분할 수 없다. 이미 켜져 있는 항목은
 * 개수가 0 이어도 **끌 수 있어야** 하므로 비활성으로 만들지 않는다.
 *
 * ## ⚠️ ns 를 **둘** 선언한다
 *
 * `useTrans` 는 `useTranslation(namespaces)` 그대로라 **선언한 ns 안에서만** 찾는다.
 * 이 레일이 그리는 라벨은 출처가 갈린다:
 *
 * ```
 * 지원 방식 · 장르 · 「필터」   → games
 * 플랫폼 이름(Meta Quest …)     → gnb        ← games 에는 없다
 * ```
 *
 * `["games"]` 만 선언하면 i18next 가 **키 문자열을 그대로 반환**한다. 키가 곧 영문
 * 라벨이라 영어 화면에서는 정상으로 보이고 **한국어에서만 조용히 영문**이 나간다.
 * `__tests__/i18n/vr-games-keys-resolve.test.ts` 가 잠근다.
 *
 * ## 2026-08-31 개정 시안 반영 (#5436)
 *
 * `29497:63500` → `29889:100934` 로 화면이 개정되면서 레일이 다시 그려졌다. 밀도만
 * 바뀐 게 아니라 **표현이 세 군데 달라졌다**:
 *
 * 1. **레일이 카드가 아니다** — 보더도 배경도 없다(`pr-40 py-20` 만 있는 열).
 *    종전엔 `border + rounded-16 + p-20` 카드였다.
 * 2. **지원 방식 행이 텍스트가 아니라 배지다** — `Native` / `Official Mod` / `MOD` /
 *    `AI Powered` 를 색 배지로 그린다. 목록 카드의 배지와 **같은 어휘**를 쓰는 게 요점이라
 *    `Badge` variant 를 그대로 재사용한다(색을 여기서 새로 정하지 않는다).
 * 3. **검색 아이콘이 오른쪽**이고 높이가 48 이다(종전 왼쪽·40).
 *
 * 밀도: 행 30→40 · 체크박스 16→**24** · group-label 24→40 · 아이콘 16→24.
 * 개수 색도 `coolgray-50` → **`coolgray-90`** 으로 진해졌다.
 */
export default function VrGamesFilterRail({
  state,
  className,
  searchDropdown,
  searchOpen,
  onSearchOpenChange,
  onSearchSubmit,
  onSearchClear,
}: {
  state: VrGamesFilterState;
  className?: string;
  /**
   * 데스크탑 슬롯의 드롭다운. 페이지가 만들어 내려보낸다 — 레일이 직접 만들면
   * 모바일 줄과 **두 벌**이 된다(`vr-games-search.tsx` 주석).
   */
  searchDropdown?: ReactNode;
  /** 검색 열림 상태·신호 — 페이지의 박스 훅이 소유한다(슬롯이 둘이라 여기 두면 갈린다). */
  searchOpen?: boolean;
  onSearchOpenChange?: (open: boolean) => void;
  /** 확정 — 페이지의 `box.submit`. 여기서 `setSearch` 를 직접 부르면 최근 검색어가 안 남는다. */
  onSearchSubmit?: () => void;
  /**
   * X(지우기) — **필수다** (#5600). 선택으로 두면 이 레일 슬롯만 조용히 빠져
   * 데스크탑에서 X 를 눌러도 `?q=` 가 남는다(실제로 그렇게 한 번 빠졌다).
   */
  onSearchClear: () => void;
}) {
  const { t } = useTrans(["games", "gnb"]);
  /**
   * 붙을 y 와 그때의 최대 높이 (#5557). 모바일은 이 레일이 `hidden` 이라 읽히지 않는다.
   */
  const railTop = useStickyBandBottom();
  const railMaxHeight = `calc(100vh - ${railTop}px)`;

  return (
    <aside
      className={cn(
        // 시안 `29889:130588` — 레일 **360**, 좌우 패딩 없음. 자식(`Search`·`rail-head`·
        // 각 그룹)이 전부 x=0 w=360 이라 내용이 레일 폭을 그대로 쓴다.
        // ★종전엔 342 + `pr-10` 이었다 — 구 시안 값이라 개정과 18px 어긋났다.
        //
        // ★**데스크탑 전용이다.** 모바일 시안(`29942:17592`)엔 레일이 없고 검색 옆
        // 필터 버튼이 바텀시트를 연다 — `VrGamesFilterSheet` 가 그 자리를 맡는다.
        "hidden w-full shrink-0 py-5 laptop:flex laptop:w-[360px]",
        /**
         * ★**스크롤해도 고정된다** (#5557 · 작성자 요청).
         *
         * `top` 은 sticky 띠의 **붙었을 때 바닥**이다 — 띠 자신이 sticky 라 그 `top` 이
         * 스크롤에 따라 움직인다(헤더가 접히면 0). 그래서 상수를 못 쓴다. 공식은
         * `sticky-band.ts` 하나(`useStickyBandBottom`).
         *
         * ⚠️ 부모가 `laptop:items-start` 여야 한다. `stretch`(기본)면 이 요소가 행 높이
         *    전체로 늘어나 **sticky 가 움직일 여지가 없다** — `games.tsx` 가 이미 그렇다.
         *
         * ★`laptop:block` → `laptop:flex` 다. 아래 그룹 목록만 스크롤시키려면 컬럼
         *  플렉스가 필요하다.
         */
        "laptop:sticky laptop:flex-col",
        className,
      )}
      style={{ top: railTop, maxHeight: railMaxHeight }}
    >
      {/**
       * ★**순서가 틀려 있었다 — 검색이 「필터」 헤더 위다.**
       *
       * 시안 좌표가 그렇게 말하고 있었는데(`Search` y=20 · `rail-head` y=84) 종전 구현은
       * 헤더를 먼저 그리고 검색을 그 아래 묶음에 넣었다. 시안을 **렌더로 보지 않고**
       * 메타데이터의 자식 순서도 확인하지 않은 채 그린 결과다.
       *
       * 좌표에서 낸 간격(앞 요소 높이를 뺀 값):
       *
       * ```
       *   rail py-20 → Search y=20  h=48        레일 상단 패딩 20
       *   Search 끝 68 → rail-head y=84         갭 16
       *   rail-head 끝 108 → 게임키 y=124 h=40   갭 16
       *   게임키 끝 164 → 지원방식 y=197         갭 33 (= mt-16 + 보더 1 + pt-16)
       *   이후 그룹 간격도 전부 33 (438-405 · 721-688 · 794-761)
       * ```
       */}
      <VrGamesSearch
        state={state}
        slot="rail"
        dropdown={searchDropdown}
        open={searchOpen}
        onOpenChange={onSearchOpenChange}
        onSubmit={onSearchSubmit}
        onClear={onSearchClear}
      />

      {/* 시안 `29889:130589` — h=24. 「필터」는 14px SemiBold `#4d5358`(coolgray-70). */}
      <div className="mt-4 flex h-6 items-center justify-between">
        <h2 className="text-base font-600 text-coolgray-70">{t("Filter")}</h2>
        <button
          type="button"
          onClick={state.reset}
          // 필터가 하나도 없으면 누를 게 없다. 숨기지 않고 비활성으로 둬서 자리가
          // 흔들리지 않게 한다.
          disabled={!state.hasAnyFilter}
          className="text-coolgray-70 transition-colors hover:text-coolgray-90 disabled:pointer-events-none disabled:text-coolgray-30"
          aria-label={t("Reset filters")}
        >
          <RefreshIcon className="h-6 w-6" />
        </button>
      </div>

      {/* 시안 `29889:130595` — h=40, 보더가 없다(개정 전엔 보더 박스). 키 아이콘이 붙었다. */}
      <div className="mt-4 flex h-10 items-center gap-2.5">
        <Checkbox
          id="vr-filter-game-key"
          className="h-6 w-6"
          checked={state.gameKeyOnly}
          onCheckedChange={state.toggleGameKey}
        />
        <label
          htmlFor="vr-filter-game-key"
          className="flex flex-1 cursor-pointer items-center gap-2 text-base text-coolgray-100"
        >
          <KeyIcon className="h-6 w-6 shrink-0" />
          {t("Game key available")}
        </label>
        <Count n={state.gameKeyCount} />
      </div>

      {/**
       * ★**여기만 스크롤한다 — 레일 전체가 아니다** (#5557).
       *
       * 레일에 `overflow-y-auto` 를 걸면 위 `VrGamesSearch` 의 드롭다운이 **잘린다** —
       * 그건 `absolute top-full` 로 **레일 안에** 그려진다
       * (`search/games-search-shell.tsx:312`).
       *
       * ⚠️ **개수를 「1건」으로 적지 말 것.** 그 서브트리의 `absolute` 는 **둘**이고 성격이
       *    다르다 — `:142` 지우기 버튼(항상) · `:312` 드롭다운 패널(열렸을 때만). 종전
       *    주석은 「1건」이라 적었는데 그건 **`height > 40` 으로 걸러 패널을 연 상태**에서
       *    센 값이었다(그 1건은 패널이다). 필터를 안 적으면 다시 재는 사람이 패널이 닫힌
       *    채로 세어 **1건 = 버튼**을 보고 결론을 뒤집는다.
       * 그래서 검색·「필터」 헤더·게임키는 흐름에 두고 그룹 목록만 스크롤한다.
       *
       * 필요한 이유(실측 2026-09-08 · 1440×900): 그룹을 전부 펼치면 레일이 **1317** 인데
       * 붙었을 때 가용 높이는 `100vh − 49 = 851` 다. 내부 스크롤이 없으면 아래쪽 필터에
       * **도달할 방법이 없다.**
       *
       * `min-h-0` 이 없으면 플렉스 자식이 안 줄어들어 `overflow` 가 발동하지 않는다.
       */}
      <div className="laptop:min-h-0 laptop:flex-1 laptop:overflow-y-auto">
        <Accordion
          type="multiple"
          defaultValue={state.groups
            .filter((g) => g.defaultOpen)
            .map((g) => g.key)}
        >
          {state.groups.map((group) => (
            <Group key={group.key} group={group} onToggle={state.toggle} />
          ))}
        </Accordion>
      </div>
    </aside>
  );
}

function Group({
  group,
  onToggle,
}: {
  group: VrFilterGroup;
  onToggle: VrGamesFilterState["toggle"];
}) {
  const { t } = useTrans(["games", "gnb"]);

  return (
    <AccordionItem
      value={group.key}
      className="mt-4 border-t border-coolgray-20 pt-4"
    >
      {/* 시안 `29889:130602` — group-label 높이 40, chevron 24. */}
      <AccordionTrigger className="h-10 px-0 py-0 hover:bg-transparent">
        <div className="flex items-center gap-1">
          {/* ★`<h3>` 가 아니라 `<span>` 이다. Radix `AccordionTrigger` 는 이미
              `AccordionHeader`(기본 `<h3>`) 안에 들어가므로 여기서 또 `<h3>` 를 쓰면
              **heading 안의 heading** 이 된다 — 실측으로 그룹 4개에 `h3` 가 8개였다.
              제목 자체는 Radix 가 제공하니 여기선 글자만 그린다. */}
          <span className="text-base font-600 text-coolgray-70">
            {t(group.label)}
          </span>
          {/* 지원 방식만 설명이 붙는다 — 시안 `29889:130708` 의 `Icons/info-24px`(20px 렌더). */}
          {group.key === "supportType" && <SupportTypeInfo group={group} />}
        </div>
        <ChevronDownIcon className="h-6 w-6" />
      </AccordionTrigger>

      <AccordionContent className="p-0">
        {group.rows.map((row) => {
          const Icon = getFilterIcon(row.value);
          // 켜져 있으면 개수가 0 이어도 끌 수 있어야 한다.
          const disabled = row.count === 0 && !row.checked;
          const id = `vr-filter-${group.key}-${row.value}`;
          return (
            /**
             * ⚠️ **행을 `<button>` 으로 감싸지 않는다.**
             *
             * Radix `Checkbox` 는 내부적으로 `<button role="checkbox">` 를 렌더한다.
             * 그걸 다시 `<button>` 으로 감싸면 **`<button>` 안의 `<button>`** 이 되고,
             * 무효 HTML 이라 React 가 hydration 을 포기한다 —
             * *"In HTML, <button> cannot be a descendant of <button>"*.
             *
             * 그러면 **그 서브트리 전체의 이벤트 핸들러가 안 붙는다.** 화면은 SSR 결과가
             * 그대로 보여서 멀쩡한데 체크박스도 아코디언도 안 눌린다. tsc·eslint·jest 는
             * 전부 통과한다(실측 2026-08-28: 이 파일 첫 판이 정확히 그 상태였다).
             *
             * `<label htmlFor>` 로 묶는다 — `<button>` 은 labelable 이라 라벨 클릭이
             * 체크박스로 전달되고, 포커스 대상도 하나로 유지된다.
             */
            <div
              key={row.value}
              className={cn(
                // 시안 `29889:130607` — 행 높이 40, 오른쪽 8 여백.
                "flex h-10 items-center gap-2.5 pr-2 transition-opacity",
                disabled && "opacity-40",
              )}
            >
              <Checkbox
                id={id}
                className="h-6 w-6"
                checked={row.checked}
                disabled={disabled}
                onCheckedChange={() => onToggle(group.key, row.value)}
              />
              <label
                htmlFor={id}
                className={cn(
                  "flex min-w-0 flex-1 items-center gap-1 text-base text-coolgray-100",
                  disabled ? "cursor-default" : "cursor-pointer",
                )}
              >
                {/**
                 * ★개정 시안에서 **지원 방식만 배지**로 그린다(`29889:130607` 의 `Badges`).
                 * 색·문구를 여기서 새로 정하지 않고 `Badge` variant 를 그대로 쓴다 —
                 * 목록 카드의 지원 타입 배지와 **같은 어휘**여야 하기 때문이다.
                 * variant 키가 facet 값(`native`·`mod`·`official-mod`·`ai-powered`)과
                 * 같아서 매핑 표가 필요 없다.
                 *
                 * ⚠️ 다만 `as never` 가 **그 「같다」를 tsc 검사에서 빼낸다.** facet 이
                 * 하나 늘면 라벨 매핑(`Record<SupportTypeFacet, …>`)은 tsc 가 잡지만
                 * 배지 variant 는 삼켜져서 **색만 조용히 사라진다.**
                 * `__tests__/v2-components/support-type-badge-variants.test.ts` 가 잠근다.
                 */}
                {group.key === "supportType" ? (
                  <Badge
                    variant={row.value as never}
                    size="sm"
                    className="whitespace-nowrap rounded-[4px]"
                  >
                    {t(row.label)}
                  </Badge>
                ) : (
                  <>
                    {Icon && <Icon className="h-6 w-6 shrink-0" aria-hidden />}
                    <span className="truncate">{t(row.label)}</span>
                  </>
                )}
              </label>
              <Count n={row.count} />
            </div>
          );
        })}
      </AccordionContent>
    </AccordionItem>
  );
}

function SupportTypeInfo({ group }: { group: VrFilterGroup }) {
  const { t } = useTrans(["games", "gnb"]);
  return (
    <HoverCard openDelay={0}>
      <HoverCardTrigger asChild>
        <span className="inline-flex">
          <InfoIcon className="h-5 w-5 text-coolgray-40" />
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        align="start"
        className="max-w-[360px] rounded-2xl border border-coolgray-20 bg-white p-6 text-start shadow-[0px_4px_8px_0px_#00000026]"
      >
        <h4 className="text-lg font-500">{t("Support Type")}</h4>
        {/* 설명 문구는 `SUPPORT_TYPE_DESC` 하나만 쓴다 — 필터 시트도 같은 걸 쓴다.
            여기 다시 쓰면 같은 개념 설명이 화면마다 갈라진다. */}
        {group.rows.map((row) => (
          <div key={row.value} className="mt-6 flex flex-col gap-2">
            <h5 className="text-lg font-400">{t(row.label)} Support</h5>
            <p className="text-base text-coolgray-70">
              {t(SUPPORT_TYPE_DESC[row.value])}
            </p>
          </div>
        ))}
      </HoverCardContent>
    </HoverCard>
  );
}

/** 시안 `29889:130611` — 12px `#21272a`(coolgray-90). 개정 전엔 coolgray-50 이었다. */
const Count = ({ n }: { n: number }) => (
  <span className="shrink-0 text-sm tabular-nums text-coolgray-90">{n}</span>
);
